<template>
	<DataTable
		:value="locators"
		:loading="loading"
		class="p-datatable-sm p-datatable-documents"
		:autoLayout="true"
		columnResizeMode="fit"
		@row-click="onRowClick"
		:scrollable="true"
		scrollHeight="flex"
		:removableSort="true"
		filterDisplay="row"
		:rowHover="true"
		v-model:filters="filters"
		@filter="onFilter($event)"
		@sort="onSort($event)"
		:sortField="currentSortField"
		:sortOrder="currentSortOrder"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.Locators.Locators') }) }}
		</template>
		<template #footer>
			<Button :label="$t('Squeeze.General.NewEntry')" type="button" icon="mdi mdi-plus" class="p-button" @click="openEntryDialog" />
			<Button :label="$t('Squeeze.System.Translations')" type="button" icon="mdi mdi-translate" class="p-button p-ml-2" @click="openTranslations" />
			<Button v-if="showBtnClearFilter" :label="$t('Squeeze.General.ClearFilters')" type="button" icon="mdi mdi-filter-off-outline" class="p-button p-ml-2" @click="clearFilters"/>
			<Button v-if="store.state.featureSet.uiConfigExport" :disabled="loading || !locators.length" label="Lokatoren exportieren" type="button" icon="mdi mdi-database-export" class="p-button p-ml-2" @click="exportAllLocators" />
			<Button v-tooltip.bottom="$t('Squeeze.Validation.Buttons.ResetTableSettings')" type="button" icon="mdi mdi-sort-variant-remove" class="p-button p-button-text p-ml-2" @click="resetTableSettings" />
		</template>

		<Column field="id" header="ID" filterField="id" :sortable="true" :showFilterMenu="false" style="min-width: 4rem; max-width: 4rem;">
			<template #body="slotProps">
				{{slotProps.data.id}}
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 3rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="name" filterField="name" :header="$t('Squeeze.Locators.Name')" :frozen="frozenColumns.name" :sortable="true" :showFilterMenu="false" style="min-width: 10rem; z-index: 1;">
			<template #header>
				<i :class="frozenColumns.name ? 'mdi mdi-lock-outline' : 'mdi mdi-lock-open-variant-outline'"
					style="margin-left: 4.5rem;"
					class="frozenIcon"
					@click="setFrozenColumns('name')"
				></i>
			</template>
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.name">
					{{slotProps.data.name}}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 9rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="description" filterField="description" :header="$t('Squeeze.Locators.Description')" :frozen="frozenColumns.description" :sortable="true" :showFilterMenu="false" style="min-width: 10rem; z-index: 1;">
			<template #header>
				<i :class="frozenColumns.description ? 'mdi mdi-lock-outline' : 'mdi mdi-lock-open-variant-outline'"
					style="margin-left: 8rem;"
					class="frozenIcon"
					@click="setFrozenColumns('description')"
				></i>
			</template>
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getTranslatedDescription(slotProps.data)">
					{{ getTranslatedDescription(slotProps.data) }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 9rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="locatorType" filterField="locatorType" :header="$t('Squeeze.Locators.LocatorType')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getLocatorTypeDescription(slotProps.data.locatorType)">
					{{ getLocatorTypeDescription(slotProps.data.locatorType) }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<Dropdown
					v-model="filterModel.value"
					@change="filterCallback()"
					:options="locatorTypes"
					class="p-column-filter filter-field"
					optionValue="value"
					optionLabel="description"
					:showClear="true"
					:filter="true"
					:autoFilterFocus="true"
					style="min-width: 9rem;"
					@show="showDropdownOverlay"
				/>
			</template>
		</Column>
		<Column field="dataType" filterField="dataType" :header="$t('Squeeze.Locators.DataType')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				{{ $t('Squeeze.General.' + slotProps.data.dataType) }}
			</template>
			<template #filter="{filterModel, filterCallback}">
				<Dropdown
					v-model="filterModel.value"
					@change="filterCallback()"
					:options="dataTypes"
					class="p-column-filter filter-field"
					optionValue="id"
					optionLabel="text"
					:showClear="true"
					style="min-width: 9rem;"
				/>
			</template>
		</Column>
		<Column field="active" filterField="active" :header="$t('Squeeze.Locators.Active')" headerClass="header-center" style="min-width: 5rem; max-width: 5rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data)" v-model="slotProps.data.active" :binary="true" :disabled="true" />
			</template>
		</Column>
		<!-- Delete & Edit -->
		<Column style="min-width: 3rem; max-width: 3rem; text-align: right;">
			<template #body="slotProps">
				<Button v-tooltip.bottom="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button-lg p-button-rounded p-button-danger p-button-text" @click="openDeleteDialog(slotProps.data)" />
			</template>
		</Column>

	</DataTable>
	<!-- Lösch-Dialog -->
	<DialogDelete :showDialog="deleteDialog" @onClose="deleteDialog = false" @onConfirm="deleteEntry()" />
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {computed, defineComponent, onMounted, PropType, reactive, ref} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import DataTable from "@/components/datatable/DexDataTable.vue";
import Column from "primevue/column";
import Checkbox from "primevue/checkbox";
import DialogDelete from "@/components/DialogDelete.vue";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import {DocumentLocator, LocatorType} from "@dex/squeeze-client-ts";
import router from "@/router";
import Tooltip from "primevue/tooltip";
import {FilterMatchMode} from "primevue/api";
import {DocumentFilterObject} from "@/apps/squeeze/interfaces/DocumentSearch";
import {showDropdownOverlay} from "@/util/StylesHelper";
import useSqueezeStore from "@/apps/squeeze/store";
import {useRoute} from "vue-router";
import {TableSettings} from "@/util/TableSettings";
import {ClientManager} from "@/singletons/ClientManager";

interface FrozenColumns {
	name: boolean;
	description: boolean;
}

export default defineComponent({
	name: "LocatorList",
	components: {
		DataTable,
		Column,
		Checkbox,
		DialogDelete,
		InputText,
		Dropdown,
	},
	props: {
		/** Show loading in the table? */
		loading: {
			type: Boolean,
			default: false,
		},
		/** All available Locators */
		locators: {
			type: Array as PropType<DocumentLocator[]>,
			default: () => [],
		},
		/** All available Locator-Types */
		locatorTypes: {
			type: Array as PropType<LocatorType[]>,
			default: () => [],
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: ["openEntryDialog", "deleteEntry", "onChangeCheckbox", "exportAllLocators", "openTranslations"],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();

		/** Current Vuex-Store */
		const store = useSqueezeStore();
		const route = useRoute();

		/** User API for persisting table settings */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current sort field */
		const currentSortField = ref<string | undefined>(undefined);

		/** Current sort order */
		const currentSortOrder = ref<number>(0);

		/** Data of selected row */
		const rowData = reactive<DocumentLocator>({});

		/** Show Delete Dialog? */
		const deleteDialog = ref<boolean>(false);

		/** Filters of list (Currently not activated) */
		const filters = ref<DocumentFilterObject>({});

		/** Should the clear filter button be shown in the table? */
		const showBtnClearFilter = ref<boolean>(false);

		/** List of Data-Types */
		const dataTypes = computed(() => {
			return [
				{
					id: 'Text',
					text: t('Squeeze.General.Text'),
				},
				{
					id: 'Date',
					text: t('Squeeze.General.Date'),
				},
				{
					id: 'Amount',
					text: t('Squeeze.General.Amount'),
				},
			];
		});

		/** Which columns are frozen? */
		const frozenColumns = reactive<FrozenColumns>({
			name: true,
			description: true,
		});

		/** Set frozen columns */
		const setFrozenColumns = (column: string) => {
			(frozenColumns as any)[column] = !(frozenColumns as any)[column];
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'frozenColumns', '', { ...frozenColumns });
		}

		/** Emits the "deleteEntry"-Event */
		const deleteEntry = () => {
			emit("deleteEntry", rowData)
		}

		/**
		 * Triggered when a checkbox is clicked
		 * @param {DocumentLocator} data Data to edit
		 */
		const onChangeCheckbox = (data: DocumentLocator) => {
			emit("onChangeCheckbox", data)
		}

		/**
		 * Gets the Description of a Locator-Type
		 * @param {number} locatorTypeId Id of the locator type
		 * @returns {String} Description of the Locator-Type or ID if not found
		 */
		const getLocatorTypeDescription = (locatorTypeId: number) => {
			if (props.locatorTypes.length > 0) {
				const locatorType: LocatorType|undefined = props.locatorTypes.find(type => type.value === locatorTypeId);
				if (locatorType) {
					return locatorType.description;
				}
			}

			return String(locatorTypeId)
		}

		/**
		 * Opens the Delete Dialog
		 * @param {DocumentLocator} data Locator that should be deleted
		 */
		const openDeleteDialog = (data: DocumentLocator) => {
			Object.assign(rowData, data);
			deleteDialog.value = true;
		}

		/**
		 * Triggered when a row is clicked
		 * @param event
		 */
		const onRowClick = (event: any) => {
			router.push({ name: 'LocatorSubView', params: { locatorId: event.data.id }})
		}

		/** Triggered when the New Entry Button is clicked */
		const openEntryDialog = () => {
			emit("openEntryDialog");
		}

		/** Opens the translations page for all locator translation keys */
		const openTranslations = () => {
			emit("openTranslations");
		}

		/**
		 * Triggered when a filter has been entered
		 * @param event
		 */
		const onFilter = (event: any) => {
			showBtnClearFilter.value = event.filteredValue.length < props.locators.length;
		}

		/** Init filters */
		const initFilters = () => {
			filters.value = {
				"id": {value: null, matchMode: FilterMatchMode.EQUALS},
				"name": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"description": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"locatorType": {value: null, matchMode: FilterMatchMode.EQUALS},
				"dataType": {value: null, matchMode: FilterMatchMode.EQUALS},
				"active": {value: null, matchMode: FilterMatchMode.CONTAINS},
			};
		}

		/** Clear filters */
		const clearFilters = () => {
			initFilters();
		}

		/** Export all locators */
		const exportAllLocators = () => {
			emit("exportAllLocators");
		}

		initFilters();

		/** Triggered on sort a column */
		const onSort = (event: { sortField: string; sortOrder: number }) => {
			currentSortField.value = event.sortField;
			currentSortOrder.value = event.sortOrder;
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'sort', '', { field: event.sortField, order: event.sortOrder }, v => !v || v.order === 0);
		}

		/** Reset sort and frozen column settings */
		const resetTableSettings = () => {
			currentSortField.value = undefined;
			currentSortOrder.value = 0;
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'sort', '', { field: '', order: 0 }, v => !v || v.order === 0);
			Object.assign(frozenColumns, { name: true, description: true });
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'frozenColumns', '', null, () => true);
		}

		onMounted(() => {
			const savedSort = TableSettings.getTableSubSetting<{ field: string; order: number }>(store, route.name as string, 'sort', '');
			if (savedSort) {
				currentSortField.value = savedSort.field;
				currentSortOrder.value = savedSort.order;
			}

			const savedFrozenColumns = TableSettings.getTableSubSetting<Record<string, boolean>>(store, route.name as string, 'frozenColumns', '');
			if (savedFrozenColumns) {
				Object.assign(frozenColumns, savedFrozenColumns);
			}
		});

		return {
			getTranslatedDescription,
			t,
			toast,
			store,
			rowData,
			deleteDialog,
			filters,
			showBtnClearFilter,
			dataTypes,
			frozenColumns,
			setFrozenColumns,
			deleteEntry,
			onChangeCheckbox,
			getLocatorTypeDescription,
			openDeleteDialog,
			onRowClick,
			openEntryDialog,
			openTranslations,
			showDropdownOverlay,
			onFilter,
			initFilters,
			clearFilters,
			exportAllLocators,
			currentSortField,
			currentSortOrder,
			onSort,
			resetTableSettings,
		};
	},
});

</script>

<style lang="scss" scoped>

// Table header
.p-datatable ::v-deep(.p-datatable-thead) {
	z-index: 2;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th) {
	background-color: var(--dex-primary-light-color);
	padding: 0.6rem 0.6rem 0.2rem 0.3rem;
	border-bottom-color: var(--dex-primary-light-color);
	color: var(--dex-text-color);
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th:first-child) {
	padding: 0.6rem 0.6rem 0.2rem 0.6rem;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-sortable-column-icon) {
	color: var(--dex-text-color);

	&:hover {
		color: var(--dex-text-color);
	}
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-inputtext.filter-field) {
	padding: 0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);
	border-style: solid;
	border-width: 0.063rem;

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}
}

.p-datatable ::v-deep(.p-datatable-thead .p-dropdown.filter-field) {
	padding:0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}

	.p-dropdown-label {
		padding: 0 1rem 0 0.4rem;
		color: var(--filter-field-color);
	}
}

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

.frozenIcon {
	color: var(--dex-text-color);
	position: absolute;

	&:hover {
		color: var(--text-color);
		opacity: 0.75;
	}
}

</style>
